import React, { useState } from "react";
import { PlateTypeCaptureProfile } from "../../../Model/CaptureProfile";
import {
  EMaskShape,
  EMaskShapeExtended,
  ImageMask,
} from "../../../Model/ImageMask";
import Collapse from "../../Common/Collapse";
import { BasicSelectionControl } from "../../Imager/ImagerFeatureControls";
import AccordionToggle from "../../Plate/AccordionToggle";

export type MaskSelection = {
  maskShapeKey: EMaskShapeExtended;
  display: string;
};

const maskSelection: MaskSelection[] = [
  {
    maskShapeKey: EMaskShapeExtended.Circle,
    display: "Circle",
  },
  {
    maskShapeKey: EMaskShapeExtended.Ellipse,
    display: "Ellipse",
  },
  {
    maskShapeKey: EMaskShapeExtended.Square,
    display: "Square",
  },
  {
    maskShapeKey: EMaskShapeExtended.Rectangle,
    display: "Rectangle",
  },
  {
    maskShapeKey: EMaskShapeExtended.RoundedSquare,
    display: "Rounded Square",
  },
  {
    maskShapeKey: EMaskShapeExtended.RoundedRectangle,
    display: "Rounded Rectangle",
  },
];

function getMaskShape(extendedMaskShape: EMaskShapeExtended) {
  switch (extendedMaskShape) {
    case EMaskShapeExtended.Ellipse:
    case EMaskShapeExtended.Circle:
      return EMaskShape.Ellipse;
    case EMaskShapeExtended.Rectangle:
    case EMaskShapeExtended.Square:
      return EMaskShape.Rectangle;
    case EMaskShapeExtended.RoundedRectangle:
    case EMaskShapeExtended.RoundedSquare:
      return EMaskShape.RoundedRectangle;
    default:
      return EMaskShape.Ellipse;
  }
}

function getExtendedImageMask(
  currentImageMask: ImageMask | undefined | null,
  extendedMaskShape: EMaskShapeExtended
): ImageMask {
  let imageMask = currentImageMask
    ? currentImageMask
    : {
        centerXOffsetInMicrons: 0,
        centerYOffsetInMicrons: 0,
        widthInMicrons: 602.3692810457517,
        heightInMicrons: 602.3692810457517,
        radiusForRoundedEdgesInMicrons: 0,
        shape: 0,
      };
  imageMask.shape = getMaskShape(extendedMaskShape);
  if (
    extendedMaskShape === EMaskShapeExtended.Circle ||
    extendedMaskShape === EMaskShapeExtended.Square ||
    extendedMaskShape === EMaskShapeExtended.RoundedSquare
  ) {
    const minWidthHeight = Math.min(
      imageMask.widthInMicrons,
      imageMask.heightInMicrons
    );
    imageMask.widthInMicrons = minWidthHeight;
    imageMask.heightInMicrons = minWidthHeight;
  }
  return imageMask;
}

type Props = {
  captureProfile: PlateTypeCaptureProfile;
  extendedMaskShape: EMaskShapeExtended;
  setExtendedMaskShape: (extendedMaskShape: EMaskShapeExtended) => void;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
};

export default function ImageMaskSelection({
  captureProfile,
  setCaptureProfile,
  extendedMaskShape,
  setExtendedMaskShape,
}: Props) {
  const [isExpand, setIsExpand] = useState(true);
  let disabled = captureProfile.plateMaskEnabled ? false : true;

  const onClick = () => setIsExpand(!isExpand);

  const onExtendedMaskShapeChanged = (
    extendedMaskShape: EMaskShapeExtended
  ) => {
    setExtendedMaskShape(extendedMaskShape);
    const imageMask = getExtendedImageMask(
      captureProfile.imageMask,
      extendedMaskShape
    );

    setCaptureProfile({
      ...captureProfile,
      imageMask: {
        ...imageMask,
      },
    });
  };

  const displayValues = maskSelection.map((mask) => {
    return {
      display: mask.display,
      value: `${mask.maskShapeKey}`,
    };
  });

  return (
    <div>
      <AccordionToggle
        expanded={isExpand}
        onClick={onClick}
        collapsible={false}
      >
        Image Mask Control
      </AccordionToggle>
      <Collapse expanded={isExpand}>
        <div style={{ marginTop: 10, marginBottom: 10 }}>
          <BasicSelectionControl
            disabled={disabled}
            feature="Shape"
            id={"IMAGE_MASK_SELECTION"}
            value={`${extendedMaskShape}`}
            displayValues={displayValues}
            onValueChanged={(id, value) =>
              onExtendedMaskShapeChanged(Number.parseInt(value))
            }
          />
        </div>
      </Collapse>
    </div>
  );
}
